import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ListChecks } from 'lucide-react';
import { tasksApi } from '@/modules/tasks/services/tasks.api';
import { TaskCard } from '@/modules/tasks/components/TaskCard';
import { TaskListSkeleton } from '@/modules/tasks/components/TaskListSkeleton';
import type { TaskItem, TaskStatus } from '@/modules/shared/types/yc-demo';

type Tab = 'pending' | 'completed' | 'all';

const TABS: Array<{ id: Tab; label: string }> = [
    { id: 'pending', label: 'Pending' },
    { id: 'completed', label: 'Completed' },
    { id: 'all', label: 'All' },
];

function readTab(value: string | null): Tab {
    return value === 'completed' || value === 'all' ? value : 'pending';
}

export function TasksPage() {
    const [params, setParams] = useSearchParams();
    const tab = readTab(params.get('status'));
    const priorityFilter = params.get('priority') === 'high' ? 'high' : null;

    const [tasks, setTasks] = useState<TaskItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);

        const status: TaskStatus | undefined = tab === 'all' ? undefined : tab;
        tasksApi
            .list({ status })
            .then((res) => {
                if (cancelled) return;
                setTasks(res);
            })
            .catch((err) => {
                if (cancelled) return;
                setError(err?.message || 'Could not reach Lantern.');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [tab]);

    const visibleTasks = useMemo(() => {
        if (!priorityFilter) return tasks;
        return tasks.filter((t) => t.priority === priorityFilter);
    }, [tasks, priorityFilter]);

    const stats = useMemo(() => {
        const pending = tasks.filter((t) => t.status === 'pending').length;
        const high = tasks.filter((t) => t.priority === 'high' && t.status !== 'completed').length;
        const dueToday = tasks.filter((t) => {
            if (!t.dueAt || t.status === 'completed') return false;
            const due = new Date(t.dueAt);
            const now = new Date();
            return (
                due.getFullYear() === now.getFullYear() &&
                due.getMonth() === now.getMonth() &&
                due.getDate() === now.getDate()
            );
        }).length;
        return { pending, high, dueToday };
    }, [tasks]);

    const subtitle = (() => {
        if (tab === 'completed') return `${tasks.length} completed`;
        if (tab === 'pending') {
            const parts: string[] = [`${stats.pending} pending`];
            if (stats.high > 0) parts.push(`${stats.high} high priority`);
            if (stats.dueToday > 0) parts.push(`${stats.dueToday} due today`);
            return parts.join(' · ');
        }
        return `${tasks.length} total`;
    })();

    return (
        <main
            style={{
                padding: '40px 32px 80px',
                maxWidth: 920,
                margin: '0 auto',
                color: 'var(--text-primary)',
            }}
        >
            <header style={{ marginBottom: 28 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <ListChecks size={20} strokeWidth={1.6} style={{ color: 'var(--accent-strong)' }} />
                    <h1
                        style={{
                            fontFamily: 'var(--v2-font-serif, serif)',
                            fontSize: 30,
                            fontWeight: 500,
                            margin: 0,
                            color: 'var(--text-primary)',
                        }}
                    >
                        Reviews waiting on you
                    </h1>
                </div>
                <p style={{ fontSize: 14, color: 'var(--text-secondary)', margin: 0 }}>{subtitle}</p>
            </header>

            <nav
                style={{
                    display: 'flex',
                    gap: 4,
                    padding: 4,
                    background: 'var(--surface-tertiary)',
                    borderRadius: 10,
                    marginBottom: 24,
                    width: 'fit-content',
                }}
                aria-label="Filter tasks"
            >
                {TABS.map((t) => {
                    const active = t.id === tab;
                    return (
                        <button
                            key={t.id}
                            type="button"
                            onClick={() => {
                                const next = new URLSearchParams(params);
                                next.set('status', t.id);
                                setParams(next);
                            }}
                            style={{
                                padding: '6px 14px',
                                borderRadius: 7,
                                border: 'none',
                                background: active ? 'var(--surface-primary)' : 'transparent',
                                boxShadow: active ? 'var(--shadow-sm)' : 'none',
                                color: active ? 'var(--text-primary)' : 'var(--text-secondary)',
                                fontSize: 13,
                                fontWeight: 500,
                                cursor: 'pointer',
                                transition: 'all 120ms ease',
                            }}
                        >
                            {t.label}
                        </button>
                    );
                })}
            </nav>

            {loading ? (
                <TaskListSkeleton />
            ) : error ? (
                <ErrorState message={error} />
            ) : visibleTasks.length === 0 ? (
                <EmptyState tab={tab} />
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {visibleTasks.map((task) => (
                        <TaskCard key={task._id} task={task} preview={(task as any).previewText ?? null} />
                    ))}
                </div>
            )}
        </main>
    );
}

function EmptyState({ tab }: { tab: Tab }) {
    const copy =
        tab === 'completed'
            ? 'No completed reviews yet. Reviews you finish appear here.'
            : tab === 'all'
              ? 'No tasks yet. Lantern surfaces decisions here when they need your eye.'
              : 'Caught up. Lantern is still working — I\'ll surface the next decision when it\'s ready.';

    return (
        <div
            style={{
                padding: '48px 24px',
                textAlign: 'center',
                background: 'var(--surface-secondary)',
                border: '1px dashed var(--border-muted)',
                borderRadius: 14,
            }}
        >
            <p
                style={{
                    fontSize: 14,
                    color: 'var(--text-secondary)',
                    margin: 0,
                    maxWidth: 380,
                    marginInline: 'auto',
                    lineHeight: 1.5,
                }}
            >
                {copy}
            </p>
        </div>
    );
}

function ErrorState({ message }: { message: string }) {
    return (
        <div
            style={{
                padding: '32px 24px',
                background: 'var(--surface-secondary)',
                border: '1px solid var(--border-muted)',
                borderRadius: 14,
            }}
        >
            <p style={{ fontSize: 14, color: 'var(--fg-danger)', margin: 0 }}>
                Couldn't reach Lantern's task list. {message}
            </p>
        </div>
    );
}
